import { parseDate } from '@eventbrite/datetime';
import { HAS_DOCUMENT } from '@eventbrite/eds-utils';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import every from 'lodash/every';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import size from 'lodash/size';
import { formatUrl } from 'url-lib';
import {
    ARTICLE_CARD_TYPE,
    CATEGORY_TYPE,
    COLLECTION_TYPE,
    DATES_LOWERCASE_MAPPING,
    DATES_UPPERCASE_MAPPING,
    DEFAULT_BLURB_NO_BUCKETS,
    DEFAULT_BLURB_NO_EVENTS,
    EVENTBRITE_CATEGORY_LOWERCASE_MAPPING,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_FORMAT_MAPPING,
    EVENT_CARD_TYPE,
    getDefaultBlurb,
    NEARBY_CITY_TYPE,
    SEARCH_TYPE_PAGE_PARAM_MAP,
    SHORT_STATES,
    TRENDING_SEARCH_TYPE,
} from '../constants';
import {
    CITY_BROWSE_SECTION,
    EVENT_AFFILIATES_MAP,
    ONLINE_SEARCH_SECTION,
    SEARCH_SECTION,
} from '../constants/analytics';
import {
    Buckets,
    BucketsContent,
    EventSalesStatus,
    EventSearch,
    Image,
    PrimaryVenue,
    TicketAvailability,
} from '../types/index';

/*********************** GENERAL UTILS ***********************/

export const createSelector = (...selectors: any[]) => (state: any) =>
    [state, ...selectors].reduce((a, b) => b(a));

/*********************** RESPONSE TRANSFORMATION ***********************/

export const createLocationTerm = (
    currentPlace: string,
    currentPlaceParent: string,
) => {
    if (currentPlaceParent) {
        return `${currentPlace}, ${currentPlaceParent}`;
    }

    return currentPlace;
};

/*
@param currentPlace :string: equivalent to currentPlace in the reducer. Where the user currently is. May not be a city.
@oaram currentPlaceParent :string: parsed from autocomplete response in utils/suggestions. Until we have city/region
pulled out in favor of our new place paradigm will need to parse this to properly generate page titles.
EB-95617

@returns :string: San Francisco, CA
*/
export const createLocationForPageTitle = (
    currentPlace: string,
    currentPlaceParent: string,
    placeType?: string,
) => {
    let modifiedPlaceParent = currentPlaceParent;

    if (currentPlaceParent) {
        //Subtitle is not a hierarchy.
        if (currentPlaceParent.indexOf(',') < 0 && placeType !== 'country') {
            if (placeType === 'locality') {
                modifiedPlaceParent = get(
                    SHORT_STATES,
                    currentPlaceParent.toLowerCase(),
                    currentPlaceParent,
                );
            }

            return createLocationTerm(currentPlace, modifiedPlaceParent);
        }

        const splitPlaceParent = currentPlaceParent
            .split(',')
            .map((term: string) => term.trim());

        if (
            (placeType === 'locality' &&
                splitPlaceParent.includes('United States')) ||
            splitPlaceParent.includes('Australia')
        ) {
            // Subtitle: "California, United States"
            modifiedPlaceParent = get(
                SHORT_STATES,
                splitPlaceParent[0].toLowerCase(),
                splitPlaceParent[0],
            );
        } else if (placeType === 'locality') {
            // Subtitle: "Paris, France"
            modifiedPlaceParent = splitPlaceParent[1];
        } else if (placeType === 'region') {
            modifiedPlaceParent = splitPlaceParent[0];
        } else {
            modifiedPlaceParent = '';
        }
    }

    return createLocationTerm(currentPlace, modifiedPlaceParent);
};

const _capitalizeFirstLetter = (string: string) =>
    string.charAt(0).toUpperCase() + string.slice(1);
const _cleanExtraSpaces = (string: string | GenericLazyString) =>
    string
        .split(' ')
        .reduce((memo: string, currentWord: string) => {
            if (currentWord) {
                return `${memo} ${currentWord}`;
            }

            return memo;
        }, '')
        .trim();

export const getSearchEventHeaderTitle = (
    {
        currentPlace,
        currentPlaceParent,
        placeType,
    }: { currentPlace: string; currentPlaceParent: string; placeType?: string },
    { eventSearch }: { eventSearch: EventSearch },
    isDocumentTitle = false,
) => {
    let { q, dates, tags, price } = eventSearch;

    const CATEGORY_MAPPING = isDocumentTitle
        ? EVENTBRITE_CATEGORY_MAPPING
        : EVENTBRITE_CATEGORY_LOWERCASE_MAPPING;
    const DATES_MAPPING: {
        [index: string]: GenericLazyString;
    } = isDocumentTitle ? DATES_UPPERCASE_MAPPING : DATES_LOWERCASE_MAPPING;

    const formatKey = tags && tags[1];
    const format = EVENTBRITE_FORMAT_MAPPING[formatKey || ''] || '';
    const query = q || '';

    let priceLabel: GenericLazyString | string = '';
    const tag = tags && tags[0];
    const category = CATEGORY_MAPPING[tag || ''] || '';
    const location = createLocationForPageTitle(
        currentPlace,
        currentPlaceParent,
        placeType,
    );

    // we allways add 'current_future' to the date filter, so when a filter is set,
    // dates becomes an array with two options: 'current_future' and the provided filter
    if (dates && isArray(dates) && size(dates)) {
        dates = last(dates);
    }

    const date = DATES_MAPPING[dates as string] || '';

    if (price && price === 'free') {
        priceLabel = gettext('Free');
    }

    if (isDocumentTitle) {
        //Page title logic prioritizes query over category but does not display both
        const titleFilter = query || category;

        if (every([query, category, date, price], isEmpty)) {
            return _cleanExtraSpaces(
                // Translators: "San Francisco, CA All Events | Eventbrite"
                gettext('%(location)s All Events | Eventbrite', {
                    location,
                }),
            );
        }

        return _cleanExtraSpaces(
            // Translators: "Free San Francisco, CA Music Festival Events This Weekend | Eventbrite"
            // Translators: "San Francisco, CA Music Festival Events This Weekend | Eventbrite"
            // Translators: "San Francisco, CA Events This Weekend | Eventbrite"
            // Translators: "San Francisco, CA Events Tomorrow | Eventbrite"
            gettext(
                '%(price)s %(location)s %(titleFilter)s %(format)s Events %(date)s | Eventbrite',
                {
                    price: priceLabel,
                    location,
                    titleFilter,
                    format,
                    date,
                },
            ),
        );
    }

    return _capitalizeFirstLetter(
        _cleanExtraSpaces(
            // Translators: "Free music events today in San Francisco, CA"
            // Translators: "Music events in San Francisco, CA"
            // Translators: "Music events tomorrow in San Francisco, CA"
            // Translators: "Events tomorrow in San Francisco, CA"
            // Translators: "Free new years eve events in San Francisco, CA"
            gettext(
                '%(price)s %(query)s %(category)s events %(date)s in %(location)s',
                {
                    price: priceLabel,
                    query,
                    category,
                    date,
                    location,
                },
            ),
        ),
    );
};

export const updateDocumentTitle = (newTitle: string) => {
    if (HAS_DOCUMENT) {
        const currentTitle = document.title;

        if (newTitle !== currentTitle) {
            document.title = newTitle;
        }
    }
};

/*
    Set page description based on initial response from the server
    and amount of content present on the page.
 */
export const getPageDescription = (
    currentPlace: string,
    buckets: Buckets | string[],
) => {
    /* City has no events and no nearby cities with events */
    let description = DEFAULT_BLURB_NO_BUCKETS;

    /* City has no events, but does have nearby cities with events */
    if (buckets && buckets.length === 1) {
        description = DEFAULT_BLURB_NO_EVENTS;
    }

    /* City has at least one bucket of events and nearby cities */
    if (buckets && buckets.length > 1) {
        description = getDefaultBlurb(currentPlace);
    }

    return description;
};

/*********************** CARD GETTERS ***********************/

export const _getUrlWithTrackingParams = (
    url: string,
    section = CITY_BROWSE_SECTION,
) =>
    formatUrl(url, {
        aff: EVENT_AFFILIATES_MAP[section],
    });

const _getImageData = (image: Image) => {
    /* Some events don't include an image at all. In which case the expansion is
   set to `null`. So if thats the case we shouldn't event attempt to access */
    let imageUrl;

    let edgeColor;

    let imageUrlOriginal;

    if (image) {
        if (image.url) {
            imageUrl = image.url;
        }

        if (image.edgeColor) {
            edgeColor = image.edgeColor;
        }

        if (image.original && image.original.url) {
            imageUrlOriginal = image.original.url;
        }
    }

    return { imageUrl, edgeColor, imageUrlOriginal };
};

export const _getPricingData = (
    ticketAvailability: TicketAvailability,
    currencyFormat: string,
    eventSalesStatus: EventSalesStatus,
    isSeries: boolean,
) => {
    let isFree = false;

    let isSoldOut = false;

    let minimumTicketPrice;

    let minPrice;

    if (eventSalesStatus && !isSeries) {
        isSoldOut = eventSalesStatus.salesStatus === 'sold_out';
    }

    if (ticketAvailability) {
        minimumTicketPrice = ticketAvailability.minimumTicketPrice;
        isFree = ticketAvailability.isFree;

        /* Only want to show the price if the event is not free */
        if (minimumTicketPrice && !isFree && minimumTicketPrice.value) {
            minPrice = {
                currencyFormat,
                minPriceValue: minimumTicketPrice.value,
                currency: minimumTicketPrice.currency,
            };
        }
    }

    return { isFree, isSoldOut, minPrice };
};

const _getVenueDetails = (primaryVenue: PrimaryVenue) => {
    let venueDetails = {};

    if (primaryVenue && primaryVenue.address) {
        const {
            name: venueName,
            address: {
                city,
                latitude,
                longitude,
                localizedAreaDisplay: venueAddress,
            },
        } = primaryVenue;

        venueDetails = { venueName, venueAddress, city, latitude, longitude };
    }

    return venueDetails;
};

export const eventsCardInfoLookup = ({
    bucketsContent,
    currencyFormat,
    section,
}: {
    bucketsContent: any[];
    currencyFormat?: string;
    section?: string;
    bucketKey?: string;
}) =>
    bucketsContent.map(
        ({
            name,
            url,
            ticketsBy,
            startDate,
            startTime,
            timezone,
            primaryVenue,
            image,
            id,
            parentUrl,
            numChildren,
            saves: { savedByYou } = { savedByYou: null },
            ticketAvailability,
            eventSalesStatus,
            isOnlineEvent,
            debugInfo,
            hideStartDate,
            dedup,
            primaryOrganizer = {},
            publicCollections,
            promotedListingMetadata,
        }) => {
            const { imageUrl, edgeColor } = _getImageData(image);
            const venueDetails = _getVenueDetails(primaryVenue);
            const isPromoted = promotedListingMetadata?.isPromotedEvent;

            const seriesUrl = parentUrl
                ? _getUrlWithTrackingParams(parentUrl, section)
                : undefined;

            const repeatingInstanceCount =
                typeof numChildren === 'number' ? numChildren - 1 : 0;

            const isSeries = repeatingInstanceCount > 0;

            const { minPrice, isFree, isSoldOut } = _getPricingData(
                ticketAvailability,
                currencyFormat || '',
                eventSalesStatus,
                isSeries,
            );

            const { numFollowers, name: organizerName } = primaryOrganizer;

            return {
                name,
                ticketsBy,
                startDate,
                startTime,
                timezone,
                imageUrl,
                isFree,
                isSoldOut,
                minPrice,
                edgeColor,
                savedByYou,
                id,
                url: _getUrlWithTrackingParams(url || '', section),
                seriesUrl,
                repeatingInstanceCount,
                isOnlineEvent,
                debugInfo,
                dedup,
                hideStartDate,
                eventSalesStatus,
                numFollowers,
                organizerName,
                publicCollections,
                isPromoted,
                promotedListingMetadata,
                ...venueDetails,
            };
        },
    );

const _articlesCardInfoLookup = ({
    bucketsContent,
}: {
    bucketsContent: BucketsContent;
}) =>
    bucketsContent.map(
        ({ id, url, image, title, byline, summary, published, tags }) => {
            const { imageUrl, edgeColor, imageUrlOriginal } = _getImageData(
                image,
            );

            return {
                url,
                id,
                imageUrl,
                imageUrlOriginal,
                edgeColor,
                byline,
                summary,
                published,
                name: title,
                tags,
            };
        },
    );

const _categoryInfoLookup = ({
    bucketsContent,
}: {
    bucketsContent: BucketsContent;
}) =>
    bucketsContent.map(({ id, url, localized: { displayName: name } }) => ({
        id,
        name,
        url,
    }));

const _nearbyCityInfoLookup = ({
    bucketsContent,
}: {
    bucketsContent: BucketsContent;
}) =>
    bucketsContent.map(({ id, name, url, subhead: distance }) => ({
        id,
        name,
        url,
        distance,
    }));

const _trendingSearchesInfoLookup = ({
    bucketsContent,
}: {
    bucketsContent: BucketsContent;
}) =>
    bucketsContent.map(
        ({ term: searchTerm, formatted: formattedSearch, searchPath, id }) => ({
            id,
            searchTerm,
            formattedSearch,
            searchPath,
        }),
    );

export const CARD_INFO_LOOKUP_MAP: { [index: string]: Function } = {
    [ARTICLE_CARD_TYPE]: _articlesCardInfoLookup,
    [EVENT_CARD_TYPE]: eventsCardInfoLookup,
    [CATEGORY_TYPE]: _categoryInfoLookup,
    [NEARBY_CITY_TYPE]: _nearbyCityInfoLookup,
    [TRENDING_SEARCH_TYPE]: _trendingSearchesInfoLookup,
    [COLLECTION_TYPE]: (content: any) => content.bucketsContent,
};

/*********************** STATE SELECTORS ***********************/

export const getDataForCurrentTypeAndPage = (
    type: string,
    paginationData: Record<string, $FixMe>,
) => {
    let data = {};
    const currentPage = paginationData.currentPage;
    const partialData = paginationData[type];

    if (!isEmpty(partialData)) {
        data = partialData[currentPage];

        if (!data) {
            data = partialData[0];
        }
    }

    return data;
};

export const getSearchContent = (
    entities: any,
    {
        currentPage,
        ...paginationData
    }: { currentPage: number; paginationData?: Record<string, $FixMe> },
    currencyFormat?: string,
    section = SEARCH_SECTION,
) =>
    Object.keys(SEARCH_TYPE_PAGE_PARAM_MAP).map((type) => {
        let result: any = { type };
        const data: any = getDataForCurrentTypeAndPage(type, {
            currentPage,
            ...paginationData,
        });

        if (!isEmpty(data)) {
            const { ids } = data;

            const bucketsContent = ids.map((id: any) => entities[id]);
            const content = CARD_INFO_LOOKUP_MAP[type]({
                bucketsContent,
                currencyFormat,
                section,
            });

            result = { ...result, content };
        }

        return result;
    });

export const getSearchSection = (isOnlineSearch: any) =>
    isOnlineSearch ? ONLINE_SEARCH_SECTION : SEARCH_SECTION;

//Utilizes the formatDate util found in dateTime but performs some extra logic
export const formatDateRangeStringForDisplay = ({
    startDate = '',
    endDate = '',
}) => {
    const { day: startDay, month: startMonth } = parseDate(startDate);
    const { day: endDay, month: endMonth } = parseDate(endDate);

    //Translators: Feb 3 - Mar 5
    let formattedDateString = gettext(
        '%(startMonth)s %(startDay)s - %(endMonth)s %(endDay)s',
        { startDay, startMonth, endDay, endMonth },
    );

    if (startDate === endDate) {
        //Translators: Feb 3
        formattedDateString = gettext('%(startMonth)s %(startDay)s', {
            startDay,
            startMonth,
        });
    }

    return formattedDateString;
};
